import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { WorkbenchBreadcrumbConfig } from '@app/models/breadcrumb.model';
import { UpliftRequestSummaryDto } from '@app/models/uplift-request-summary.model';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { UpliftService } from '@app/services/uplift/uplift.service';
import { JobStatus } from '@app/shared/enums';
import { TranslateService } from '@ngx-translate/core';
import { map, Subscription } from 'rxjs';

interface UpgradeSummaryFilters {
  jobNumber: string;
  upgradeStatus: string;
  year: number | null;
  siteName: string;
  minAmount: number | null;
  maxAmount: number | null;
}

const YEAR_OPTION_COUNT = 5;
const OFFSET_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSSZZZZZ";
const NON_NAVIGABLE_JOB_STATUSES = new Set<string>([
  JobStatus.COMPLETED,
  JobStatus.FAULT_CANCELLED,
  JobStatus.MISSED,
  JobStatus.FIXED,
  JobStatus.CANCELLED
]);

@Component({
  selector: 'app-upgrade-summary',
  templateUrl: './upgrade-summary.component.html',
  styleUrls: ['./upgrade-summary.component.scss'],
  standalone: false
})
export class UpgradeSummaryComponent implements OnInit, OnDestroy {
  upgrades: (UpliftRequestSummaryDto & { isNavigable: boolean })[] = [];
  statuses: string[] = [];
  private statusesSubscription?: Subscription;
  readonly years = Array.from({ length: YEAR_OPTION_COUNT }, (_, index) => new Date().getFullYear() - index);
  totalCount = 0;
  private loadSubscription?: Subscription;
  filters: UpgradeSummaryFilters = this.defaultFilters();
  appliedFilters: UpgradeSummaryFilters = this.defaultFilters();
  pageIndex = 0;
  pageSize = 10;
  readonly amountSliderMax = 10000;
  readonly amountSliderStep = 50;
  hasLoaded = false;
  hasLoadError = false;
  breadcrumb:WorkbenchBreadcrumbConfig;

  constructor(
    private readonly upliftService: UpliftService,
    private readonly navigationService: NavigationService,
    private readonly translate: TranslateService
  ) {
    this.navigationService.onChange(false, false);
    this.breadcrumb = {
      title: this.translate.instant("resource_admin_dashboard_upgrade_summary"),
      subtitle: this.translate.instant("upgrade_summary_subtitle"),
      breadcrumbItems: [
        {
          "label": this.translate.instant("resource_admin_dashboard_title"),
          "link": "/dashboard",
          "queryParams": {}
        },
        {
          "label": this.translate.instant("resource_admin_dashboard_upgrade_summary"),
          "link": "",
          "queryParams": {}
        },
      ]
    };
  }

  ngOnInit(): void {
    this.loadStatuses();
    this.loadUpgrades();
  }

  ngOnDestroy(): void {
    this.statusesSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
  }

  get isAmountRangeInvalid(): boolean {
    const { minAmount, maxAmount } = this.filters;
    return (minAmount != null && minAmount < 0)
      || (maxAmount != null && maxAmount < 0)
      || (minAmount != null && maxAmount != null && maxAmount < minAmount);
  }

  formatAmountLabel(value: number): string {
    return value >= 1000 ? `$${Math.round(value / 100) / 10}k` : `$${value}`;
  }

  formatLodgedOn(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    // Parsing the offset-qualified value yields a Date shown in the browser's local timezone.
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '-';
    }
    // Undefined locale = the client's runtime locale.
    return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  }

  applyFilters(): void {
    if (this.isAmountRangeInvalid) {
      return;
    }

    this.appliedFilters = { ...this.filters };
    this.pageIndex = 0;
    this.loadUpgrades();
  }

  resetFilters(): void {
    this.filters = this.defaultFilters();
    this.applyFilters();
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadUpgrades();
  }

  private loadStatuses(): void {
    this.statusesSubscription = this.upliftService.getUpgradeStatuses().subscribe({
      next: statuses => this.statuses = statuses ?? [],
      error: () => this.statuses = []
    });
  }

  private loadUpgrades(): void {
    this.loadSubscription?.unsubscribe();
    this.hasLoaded = false;
    this.hasLoadError = false;
    const { year } = this.appliedFilters;
    this.loadSubscription = this.upliftService.getUpliftRequestSummary({
      // Client-local year boundaries, sent with the local offset.
      fromDate: year != null ? this.toOffsetDateString(new Date(year, 0, 1)) : null,
      toDate: year != null ? this.toOffsetDateString(new Date(year, 11, 31, 23, 59, 59, 999)) : null,
      jobNumber: this.appliedFilters.jobNumber.trim() || null,
      upgradeStatus: this.appliedFilters.upgradeStatus || null,
      siteName: this.appliedFilters.siteName.trim() || null,
      minAmount: this.appliedFilters.minAmount,
      maxAmount: this.appliedFilters.maxAmount,
      pageNumber: this.pageIndex + 1,
      pageSize: this.pageSize
    }).pipe(
      map(page => ({
        ...page,
        results: page.results.map(upgrade => {
          const jobStatus = upgrade.jobStatus?.trim().toUpperCase();
          return {
            ...upgrade,
            isNavigable: !!jobStatus && !NON_NAVIGABLE_JOB_STATUSES.has(jobStatus)
          };
        })
      }))
    ).subscribe({
      next: page => {
        this.upgrades = page.results;
        this.totalCount = page.totalResults;
        this.hasLoaded = true;
      },
      error: () => {
        this.upgrades = [];
        this.totalCount = 0;
        this.hasLoadError = true;
        this.hasLoaded = true;
      }
    });
  }

  private toOffsetDateString(date: Date): string {
    return formatDate(date, OFFSET_DATE_FORMAT, 'en-US');
  }

  private defaultFilters(): UpgradeSummaryFilters {
    return {
      jobNumber: '',
      upgradeStatus: '',
      year: null,
      siteName: '',
      minAmount: null,
      maxAmount: null
    };
  }
}